import { DateTime } from 'luxon'

/**
 * Salesforceプロジェクトメンバータイプ ID
 */
export const SALESFORCE_PROJECT_MEMBER_TYPE_IDS = {
  CAPTAIN: 1,
  SERVICE_MANAGER: 2,
  ACCOUNT_MANAGER: 3,
  PROJECT_MANAGER: 4,
  SALES_MEMBER: 5,
} as const

export type SalesforceProjectMemberTypeId =
  (typeof SALESFORCE_PROJECT_MEMBER_TYPE_IDS)[keyof typeof SALESFORCE_PROJECT_MEMBER_TYPE_IDS]

/**
 * 役職区分ごとの job_role_id（Dracaena の group-manager-divisions.js と同じ）
 * v_accounts の is_group_manager 等が実際に立つ役職に合わせている
 * - GROUP_MANAGER: 3: グループ長、4: グループ長補佐、14: 室長
 * - DEPARTMENT_MANAGER: 17: 部長、9: 副部長
 * - DIVISION_HEAD: 6: 事業部長、16: 統括部長、7: 副事業部長
 */
export const MANAGER_JOB_ROLE_IDS = {
  GROUP_MANAGER: [3, 4, 14],
  DEPARTMENT_MANAGER: [17, 9],
  DIVISION_HEAD: [6, 16, 7],
} as const

/**
 * G長補佐以上の役職判定から除外する job_role_id
 * - 1: VPoHR、2: イニシアティブ長
 */
export const NON_MANAGER_JOB_ROLE_IDS = [1, 2] as const

/**
 * オポチュニティタイプID
 */
export const OPPORTUNITY_TYPE_IDS = {
  CAPTAIN: 1,
  SALES: 2,
} as const

export type OpportunityTypeId = (typeof OPPORTUNITY_TYPE_IDS)[keyof typeof OPPORTUNITY_TYPE_IDS]

/**
 * Salesforce Sales Stage IDs
 */
export const SALES_STAGE_IDS = {
  SS1: 1,
  SS2: 2,
  SS3: 3,
  SS4: 4,
  SS5: 5,
  SS6: 6,
  SS7: 7,
  DUPLICATED: 8,
  INPUT_ERROR: 9,
} as const

export type SalesStageId = (typeof SALES_STAGE_IDS)[keyof typeof SALES_STAGE_IDS]

/**
 * job_roles の ID
 */
export const JOB_ROLE_IDS = {
  VPOHR: 1, // VPoHR
  INITIATIVE_LEADER: 2, // イニシアティブ長
  GROUP_LEADER_ASSISTANT: 4, // グループ長補佐
  DIVISION_DIRECTOR: 6, // 事業部長
} as const

/**
 * Salesforce Sales Stage マップ
 * 各SalesStageの表示名とツールチップ情報を提供
 */
export const SALES_STAGE_MAP: Record<number, { text: string; tooltip: string }> = {
  1: { text: 'SS1', tooltip: '受注予測(確度低)になったことを意味します。' },
  2: { text: 'SS2', tooltip: '受注予測(確度中)になったことを意味します。' },
  3: { text: 'SS3', tooltip: '受注予測(確度高)になったことを意味します。' },
  4: { text: 'SS4', tooltip: '見積書提出になったことを意味します。' },
  5: { text: 'SS5', tooltip: '内示(稼働開始)になったことを意味します。' },
  6: { text: 'SS6', tooltip: '受注になったことを意味します。' },
  7: { text: 'SS7', tooltip: '検収済みになったことを意味します。' },
  8: { text: 'Duplicated', tooltip: '重複入力、別売上情報統合を意味します' },
  9: { text: 'InputError', tooltip: '誤登録を意味します' },
  10: { text: 'SQL', tooltip: '商談化されたことを意味します。' },
  11: { text: 'LOST', tooltip: '失注になったことを意味します。' },
} as const

/**
 * オポチュニティ用Salesforce Sales Stage マップ
 * 各SalesStageの表示名とツールチップ情報を提供
 */
export const OPPORTUNITY_SALES_STAGE_MAP: Record<number, { text: string; tooltip: string }> = {
  1: { text: 'SS1', tooltip: 'オポが受注予測(確度低)になったことを意味します。' },
  2: { text: 'SS2', tooltip: 'オポが受注予測(確度中)になったことを意味します。' },
  3: { text: 'SS3', tooltip: 'オポが受注予測(確度高)になったことを意味します。' },
  4: { text: 'SS4', tooltip: 'オポが見積書提出になったことを意味します。' },
  5: { text: 'SS5', tooltip: 'オポが内示(稼働開始)になったことを意味します。' },
  6: { text: 'SS6', tooltip: 'オポが受注になったことを意味します。' },
  7: { text: 'SS7', tooltip: 'オポが検収済みになったことを意味します。' },
  8: { text: 'Duplicated', tooltip: '報酬対象外です' },
  9: { text: 'InputError', tooltip: '報酬対象外です' },
  10: { text: 'SQL', tooltip: 'オポが商談化されたことを意味します。' },
  11: { text: 'LOST', tooltip: 'オポが失注になったことを意味します。' },
} as const

/**
 * インセンティブ確認画面用 Salesforce Sales Stage マップ
 * 各SalesStageの表示名と係数情報を含むツールチップを提供
 * Duplicated(8)とInputError(9)は報酬対象外のため除外
 */
export const INCENTIVE_SALES_STAGE_MAP: Record<number, { text: string; tooltip: string }> = {
  1: { text: 'SS1', tooltip: 'オポが受注予測(確度低)になったことを意味します。係数は0.2％です。' },
  2: { text: 'SS2', tooltip: 'オポが受注予測(確度中)になったことを意味します。係数は0.2％です。' },
  3: { text: 'SS3', tooltip: 'オポが受注予測(確度高)になったことを意味します。係数は0.2％です。' },
  4: { text: 'SS4', tooltip: 'オポが見積書提出になったことを意味します。係数は1.0％です。' },
  5: { text: 'SS5', tooltip: 'オポが内示(稼働開始)になったことを意味します。係数は1.0％です。' },
  6: { text: 'SS6', tooltip: 'オポが受注になったことを意味します。係数は1.0％です。' },
  7: { text: 'SS7', tooltip: 'オポが検収済みになったことを意味します。係数は1.0％です。' },
  10: { text: 'SQL', tooltip: 'オポが商談化されたことを意味します。係数は0.2％です。' },
  11: { text: 'LOST', tooltip: 'オポが失注になったことを意味します。係数は失注前Sales Stageに準じます。' },
} as const

/**
 * アクションタイプ
 */
export const ACTION_TYPES = {
  CREATE: 1,
  UPDATE: 2,
  DELETE: 3,
  PASSWORD_LOGIN: 4,
  TOKEN_LOGIN: 5,
  LOGIN_FAILED: 6,
  LOGOUT: 7,
  PASSWORD_CHANGE: 8,
  EXCEL_UPLOAD: 9,
  MOVE: 10,
  ONELOGIN_LOGIN: 11,
} as const

export type ActionType = (typeof ACTION_TYPES)[keyof typeof ACTION_TYPES]

/**
 * アクションカテゴリ
 */
export const ACTION_CATEGORIES = {
  ACCOUNT: 1,
  FREE_ASSIGN: 2,
  LOGIN: 3,
  LOGOUT: 4,
  MEMBER: 5,
  ASSIGN: 6,
  PROJECT: 7,
  SALESFORCE_PROJECT: 8,
  SALESFORCE_PROJECT_MEMBER: 9,
  OPPORTUNITY: 10,
  OPPORTUNITY_COMMENT: 11,
  OPPORTUNITY_TASK: 12,
  PROJECT_DECISION: 13,
  PROJECT_COMMENT: 14,
  PKS_WEEKLY_REPORT: 15,
  PKS_WEEKLY_REPORT_RESULT_CHECK: 16,
  TEAMS_GROUP_CHAT: 17,
  SALESFORCE_PROJECT_FILE: 18,
  CAT_TENANT: 19,
  SALESFORCE_PROJECT_KICKOFF: 20,
  SALESFORCE_PROJECT_POSTMORTEM: 21,
  ACHIEVEMENT: 22,
  DIVISION: 23,
  FOLLOW_MEMBER: 24,
  OPPORTUNITY_INCENTIVE_ACCEPT: 25,
  INQUIRY: 27,
} as const

export type ActionCategory = (typeof ACTION_CATEGORIES)[keyof typeof ACTION_CATEGORIES]

/**
 * 引合アサインステータス定義テーブル
 * id / key / name / color を一元管理し、個別の定数はここから導出する
 */
export const INQUIRY_ASSIGN_STATUS_DEFS = [
  { id: 1, key: 'SEARCHING', name: '探し中', color: 'orange' },
  { id: 2, key: 'ADJUSTING', name: '調整中', color: 'amber' },
  { id: 3, key: 'CONFIRMED', name: '確定', color: 'green' },
  { id: 4, key: 'WITHDRAWN', name: '取り下げ', color: 'gray' },
] as const

/** ステータス名の型 */
export type InquiryAssignStatus = (typeof INQUIRY_ASSIGN_STATUS_DEFS)[number]['name']

/** ステータスID の型 */
export type InquiryAssignStatusId = (typeof INQUIRY_ASSIGN_STATUS_DEFS)[number]['id']

/** key → ID マッピング */
type InquiryAssignStatusMap = { readonly [D in (typeof INQUIRY_ASSIGN_STATUS_DEFS)[number] as D['key']]: D['id'] }
export const INQUIRY_ASSIGN_STATUSES: InquiryAssignStatusMap = Object.fromEntries(
  INQUIRY_ASSIGN_STATUS_DEFS.map((d) => [d.key, d.id]),
) as InquiryAssignStatusMap

/** ステータス名 → 色マッピング */
type InquiryAssignStatusColorMap = {
  readonly [K in InquiryAssignStatus]: (typeof INQUIRY_ASSIGN_STATUS_DEFS)[number]['color']
}
export const INQUIRY_ASSIGN_STATUS_COLOR_MAP: InquiryAssignStatusColorMap = Object.fromEntries(
  INQUIRY_ASSIGN_STATUS_DEFS.map((d) => [d.name, d.color]),
) as InquiryAssignStatusColorMap

/** ステータス名の配列（z.enumで使用） */
export const INQUIRY_ASSIGN_STATUS_NAMES = INQUIRY_ASSIGN_STATUS_DEFS.map((d) => d.name)

/**
 * 引合アサインのデフォルト値
 */
export const INQUIRY_ASSIGN_DEFAULTS = {
  /** 単価初期値 */
  UNIT_PRICE: '0',
  /** 人月初期値 */
  PERSON_MONTH: '1.00',
} as const

/** 確定・取り下げ済みステータス */
export const INQUIRY_COMPLETED_STATUS_IDS = new Set<number>([
  INQUIRY_ASSIGN_STATUSES.CONFIRMED,
  INQUIRY_ASSIGN_STATUSES.WITHDRAWN,
])

/** ANO に紐づく引合のステータス（進行中 / 確定済） */
export const ANO_INQUIRY_STATUSES = {
  IN_PROGRESS: 'in_progress',
  CONFIRMED: 'confirmed',
} as const
export type AnoInquiryStatus = (typeof ANO_INQUIRY_STATUSES)[keyof typeof ANO_INQUIRY_STATUSES]

/**
 * エラーメッセージ
 */
export const ERROR_MESSAGES = {
  // 共通
  INTERNAL_SERVER_ERROR: 'internal server error',
  INVALID_USER: 'invalid user',
  INVALID_DATE_FORMAT: 'invalid date format',
  INVALID_DATE_RANGE: 'startOn must be before or equal to endOn',
  START_ON_REQUIRED_WITH_END_ON: 'startOn is required when endOn is specified',
  UNAUTHORIZED_ACCESS: 'Unauthorized access.',
  AUTHORIZATION_HEADER_UNDEFINED: 'Authorization header undefined',
  DATA_ALREADY_EXISTS: 'data already exists',
  MEMBER_NOT_FOUND: 'member not found',
  DIVISION_NOT_FOUND: 'division not found',
  // Account
  SAME_PASSWORD: 'same password',
  INVALID_PASSWORD: 'invalid password',
  ACCOUNT_NOT_FOUND: 'data not found',
  EMAIL_REQUIRED: 'email is required',
  INVALID_EMAIL: 'invalid email',
  EMAIL_ALREADY_EXISTS: 'already exists',
  MEMBER_ID_ALREADY_EXISTS: 'already exists',
  // Salesforce
  PROJECT_NOT_FOUND: 'salesforce project not found',
  PROJECT_MEMBER_NOT_FOUND: 'salesforce project member not found',
  PROJECT_MEMBER_TYPE_NOT_FOUND: 'salesforce project member type not found',
  EXPIRED_ON_INVALID: 'expiredOn must be tomorrow or later',
  MEMBER_CANNOT_BE_DELETED: 'this member cannot be deleted',
  CANNOT_GET_OPPORTUNITY_MEMBER: 'cannot get this opportunity member',
  // Opportunity
  OPPORTUNITY_NOT_FOUND: 'opportunity not found',
  CANNOT_GET_OPPORTUNITY: 'cannot get this opportunity',
  // Inquiry
  CUSTOMER_NOT_FOUND: 'salesforce customer not found',
  DEAL_NOT_FOUND: 'salesforce deal not found',
  PROJECT_DEAL_NOT_FOUND: 'deal not found for this project',
  PROJECT_DEAL_MISMATCH: 'project and deal combination is invalid',
  CONTACT_NOT_FOUND: 'contact not found',
  INQUIRY_NOT_FOUND: 'inquiry not found',
  INQUIRY_RELATED_DIVISION_CREATE_FAILED: 'failed to create inquiry related division',
  INQUIRY_STATUS_NOT_FOUND: 'inquiry status not found',
  INQUIRY_COMPLETED_CONSTRAINT: 'cannot modify fields on completed inquiry',
  INQUIRY_CLOSE_REASON_REQUIRED: 'inquiryCloseReason is required when changing status to withdrawn',
  INQUIRY_DEAL_REQUIRED_FOR_PROJECT: 'deal is required to set salesforceProjectId on inquiry',
  INQUIRY_DEAL_REQUIRED_FOR_CONFIRM: 'deal is required to confirm inquiry',
  INQUIRY_DEAL_CUSTOMER_MISMATCH: 'deal does not belong to the customer of inquiry',
  INQUIRY_DEAL_OR_CUSTOMER_REQUIRED: 'dealId or salesforceCustomerId is required',
  INQUIRY_RELATED_DIVISION_NOT_FOUND: 'inquiry related division not found',
  COORDINATION_NOT_FOUND: 'inquiry coordination not found',
  INQUIRY_COORDINATION_CREATE_FAILED: 'failed to create inquiry coordination',
  INQUIRY_ASSIGN_STATUS_NOT_FOUND: 'inquiry assign status not found',
  FAILED_TO_CREATE_INQUIRY_ASSIGN: 'failed to create inquiry assign',
  INQUIRY_ASSIGN_NOT_FOUND: 'inquiry assign not found',
  NO_ACTIVE_INQUIRY_ASSIGN: 'no active inquiry assign exists',
  INQUIRY_ASSIGN_DUPLICATE: 'inquiry assign already exists',
  ROLE_NOT_FOUND: 'role not found',
  SALES_STATUS_NOT_FOUND: 'sales status not found',
  SALES_RECORDING_GROUP_NOT_FOUND: 'sales recording group not found',
  CANNOT_UPDATE_INQUIRY_ASSIGN: 'cannot update inquiry assign',
  CANNOT_GET_INQUIRY_COORDINATION: 'cannot get updated inquiry coordination',
  INQUIRY_CREATE_FAILED: 'failed to create inquiry',
  INQUIRY_UPDATE_FAILED: 'failed to update inquiry',
  INQUIRY_RELATED_DIVISION_UPDATE_FAILED: 'failed to retrieve updated record',
} as const

export type ErrorMessage = (typeof ERROR_MESSAGES)[keyof typeof ERROR_MESSAGES]

/**
 * オポチュニティタスクステータス ID
 */
export const OPPORTUNITY_TASK_STATUS_IDS = {
  OPEN: 1, // 未着手
  IN_PROGRESS: 2, // 着手中
  ON_HOLD: 3, // 保留中
  CANCELLED: 4, // 対応中止
  COMPLETED: 5, // 対応完了
} as const

export type OpportunityTaskStatusId = (typeof OPPORTUNITY_TASK_STATUS_IDS)[keyof typeof OPPORTUNITY_TASK_STATUS_IDS]

/**
 * オポチュニティステータス ID
 */
export const OPPORTUNITY_STATUS_IDS = {
  OPEN: 1, // Open
  CLOSE: 2, // Close
  PENDING: 3, // Pending
  SQL_REGISTERED: 4, // SQL登録完了
  DUPLICATE: 5, // 重複
  SFA_NON_SQL: 6, // SFA登録済（非SQL）
  GROUP_COMPANY: 7, // G会社商流
} as const

export type OpportunityStatusId = (typeof OPPORTUNITY_STATUS_IDS)[keyof typeof OPPORTUNITY_STATUS_IDS]

/**
 * フィールド最大文字数制限
 * フロントエンド・バックエンド共通で使用する文字数制限値
 */
export const FIELD_MAX_LENGTHS = {
  /** オポチュニティタイトル */
  OPPORTUNITY_TITLE: 128,
  /** 情報の入手元 */
  OPPORTUNITY_INFORMATION_SOURCE: 128,
  /** オポチュニティ説明文、クローズ理由詳細 */
  OPPORTUNITY_DESCRIPTION: 10240,
  /** 担当者名 */
  OPPORTUNITY_CONTACT_NAME: 64,
  /** タスクタイトル */
  OPPORTUNITY_TASK_TITLE: 128,
  /** コメント */
  COMMENT: 1024,
  /** 汎用名称（短）- ステータス名等 */
  NAME_SHORT: 64,
  /** 汎用名称（標準）- プロジェクト名等 */
  NAME_STANDARD: 255,
  /** 汎用説明（短）- 部門名、情報源等 */
  DESCRIPTION_SHORT: 512,
  /** 顧客名 */
  CUSTOMER_NAME: 128,
  /** パスワード */
  PASSWORD: 32,
  /** ヒント */
  HINT: 256,
  /** 引合調整状況内容 */
  INQUIRY_COORDINATION_CONTENT: 128,
  /** SF商談ID */
  SALESFORCE_DEAL_ID: 18,
  /** SF商談名 */
  SALESFORCE_DEAL_NAME: 256,
  /** ANO */
  ANO: 16,
  /** 売上ID */
  SALES_ID: 32,
  /** 汎用名称（中）- industry_name, station, service_name, email等 */
  NAME_MEDIUM: 128,
  /** 引合要件 */
  INQUIRY_REQUIREMENTS: 10240,
  /** 引合案件概要 */
  INQUIRY_OVERVIEW: 10240,
  /** 引合備考 */
  INQUIRY_NOTES: 1024,
  /** 引合想定金額 */
  INQUIRY_ESTIMATED_AMOUNT: 1_000_000_000,
} as const

export type FieldMaxLength = (typeof FIELD_MAX_LENGTHS)[keyof typeof FIELD_MAX_LENGTHS]

/**
 * フィールド最小文字数制限
 * フロントエンド・バックエンド共通で使用する文字数制限値
 */
export const FIELD_MIN_LENGTHS = {
  /** 現在のパスワード（過去のポリシーで作成されたパスワードを受け付けるため、最小文字数を低く設定） */
  OLD_PASSWORD: 3,
  /** 新しいパスワード（現行のセキュリティポリシー） */
  NEW_PASSWORD: 8,
} as const

export type FieldMinLength = (typeof FIELD_MIN_LENGTHS)[keyof typeof FIELD_MIN_LENGTHS]

/**
 * 顧客名検索（salesforce_customers）で一度に取得する候補件数の上限。
 * 顧客は数万件規模あるため全件取得せず、検索クエリで絞り込んだ先頭 N 件のみを返す。
 */
export const SALESFORCE_CUSTOMER_SEARCH_LIMIT = 50

/**
 * オポチュニティステータスID
 */
export const OPPORTUNITY_STATUS_ID = {
  OPEN_STATUS_ID: 1,
  CLOSED_STATUS_ID: 2,
  PENDING_STATUS_ID: 3,
  COMPLETE_SQL_REGISTRATION_STATUS_ID: 4,
  DUPLICATION_STATUS_ID: 5,
  COMPLETE_SFA_REGISTRATION_STATUS_ID: 6,
  G_COMPANY_STATUS_ID: 7,
}

/**
 * オポチュニティのクローズ理由
 */
export const OPPORTUNITY_CLOSE_REASONS = {
  ADDITIONAL_CLOSE_REASON_ID: 12, // その他理由
}

/**
 * バリデーションメッセージ（インラインエラー用 - フィールド名なし）
 */
export const VALIDATION_MESSAGES = {
  REQUIRED: '必須項目です（スペースのみの入力は無効です）',
  REQUIRED_SELECT: '必須項目です',
  INVALID_SELECT: '選択肢から選択してください',
  minLength: (n: number) => `${n}文字以上で入力してください`,
  maxLength: (n: number) => `${n}文字以内で入力してください`,
  maxAmount: (n: number) => `${n.toLocaleString()}以下で入力してください`,
  REQUIRE_ACTIVE_ASSIGN_FOR_CONFIRMED: '確定にするには、取り下げ以外のアサインが必要です',
} as const

/**
 * バリデーションメッセージ（toast用 - フィールド名あり）
 */
export const TOAST_VALIDATION_MESSAGES = {
  required: (fieldName: string) => `${fieldName}は必須です`,
  requiredInput: (fieldName: string) => `${fieldName}を入力してください`,
  maxLength: (fieldName: string, n: number) => `${fieldName}は${n}文字以内で入力してください`,
} as const

/**
 * 引合追加・編集メッセージ
 */
export const PKS_MESSAGES = {
  EDIT_SALES_SMG_IN_PKS: '営業担当・SMG担当はPKSで編集してください',
  EDIT_PRIMARY_DIVISION_IN_PKS: '主管部門はPKSで編集してください',
  MEMBER_NOT_REGISTERED: 'メンバーは登録されていません',
} as const

/**
 * アサイン追加・編集のメンバー候補メッセージ
 */
export const INQUIRY_ASSIGN_MEMBER_MESSAGES = {
  CANDIDATE_SCOPE:
    '主管部門・関連部門の所属者のみ表示されます。\n候補を広げるには基本情報タブで関連部門を追加してください',
  NO_ASSIGNABLE_MEMBERS:
    '該当部門にアサイン可能なメンバーがいません。\n基本情報タブで関連部門を追加すると候補が広がります',
  FETCH_FAILED: '候補の取得に失敗しました。\n画面を再読み込みしてください',
  SEARCH_NO_MATCH: '検索条件に一致するメンバーが見つかりません',
} as const

/**
 *当日、当日から1年後、当日の1年前の日付
 */
export const DATE = {
  today: DateTime.now().toISODate(),
  oneYearAgo: DateTime.now().minus({ years: 1 }).toISODate(),
  oneYearLater: DateTime.now().plus({ years: 1 }).toISODate(),
}

/**
 * 認証エラーメッセージ
 */
export const AUTH_ERROR_MESSAGE = '認証トークンが見つかりません' as const

/**
 * オポチュニティ情報源の選択肢
 */
export const INFORMATION_SOURCE_OPTIONS = ['自らの気づき', 'お客様との会話', 'アサインメンバーとの会話'] as const

export type InformationSourceOption = (typeof INFORMATION_SOURCE_OPTIONS)[number]

/**
 * 引合ロール種別
 */
export const INQUIRY_ROLE_VALUES = ['sales', 'smg', 'manager'] as const

export type InquiryRole = (typeof INQUIRY_ROLE_VALUES)[number]

/**
 * 引合優先度
 */
export const PRIORITY_VALUES = ['high', 'medium', 'low'] as const

export type InquiryPriority = (typeof PRIORITY_VALUES)[number]

export const INQUIRY_PRIORITY_LABEL_MAP = {
  high: '高',
  medium: '中',
  low: '低',
} as const satisfies Record<InquiryPriority, string>

export const INQUIRY_PRIORITY_COLOR_MAP = {
  high: 'destructive',
  medium: 'amber',
  low: 'blue',
} as const satisfies Record<InquiryPriority, string>

/**
 * 引合の募集形態（社員だけを出せる案件か、BP も出せる案件か）
 * 未設定（NULL）を許すため、優先度と違い既定値は持たない
 */
export const RECRUITMENT_TYPE_VALUES = ['employee_only', 'bp_allowed'] as const

export type InquiryRecruitmentType = (typeof RECRUITMENT_TYPE_VALUES)[number]

export const INQUIRY_RECRUITMENT_TYPE_LABEL_MAP = {
  employee_only: '社員限定',
  bp_allowed: 'BP可',
} as const satisfies Record<InquiryRecruitmentType, string>

/**
 * 募集形態のバッジ色
 * 雇用形態バッジ（AppBadgeForEmployeeType）の正社員・BP と同じ色系統にそろえる。
 * AppSelectPopover の選択肢は BadgeColor しか受け取れないため、色トークンではなく BadgeColor で持つ
 */
export const INQUIRY_RECRUITMENT_TYPE_COLOR_MAP = {
  employee_only: 'blue',
  bp_allowed: 'orange',
} as const satisfies Record<InquiryRecruitmentType, string>

/** 募集形態が未設定のときに選択 UI に出す表示（一覧では「-」と表示する） */
export const INQUIRY_RECRUITMENT_TYPE_UNSET_LABEL = '未設定'

/**
 * 引合の業界（lobby の業界体系の大分類。並び順は lobby の INDUSTRY_GROUPS の定義順）
 * 人材側の業界経験と同じ語彙にしてマッチングに使えるようにするため、中分類（金融/保険 等）は持たない
 */
export const INDUSTRY_GROUPS = [
  '情報・通信',
  '製造',
  '金融',
  'サービス',
  'インフラ',
  '流通・小売',
  '官公庁・自治体・団体',
  'エンタメ',
  '商社',
] as const

export type IndustryGroup = (typeof INDUSTRY_GROUPS)[number]

/**
 * 作業場所（オンサイト/オフサイト/在宅）
 */
export type WorkLocations = {
  onsite: boolean
  offsite: boolean
  remote: boolean
}

export const DEFAULT_WORK_LOCATIONS: WorkLocations = {
  onsite: false,
  offsite: false,
  remote: false,
}

/** 作業場所の表示ラベル */
export const WORK_LOCATION_LABELS: Record<keyof WorkLocations, string> = {
  onsite: 'オンサイト',
  offsite: 'オフサイト',
  remote: '在宅',
}

/**
 * 引合の開始日「なる早」（日付は未定だが、できるだけ早く）の表示ラベル
 * - LABEL: 一覧の期間列・検索バッジ用。改善要望の原文どおりの語で、現場では説明なしで通じる
 * - CHECKBOX_LABEL: 登録・詳細のチェックボックス用。意味の補足はここにだけ置く
 */
export const INQUIRY_START_ASAP_LABELS = {
  LABEL: 'なる早',
  CHECKBOX_LABEL: 'なる早（開始日未定・できるだけ早く）',
} as const

/**
 * 引合の検索条件「商談未紐付け」の表示ラベル（検索ダイアログのチェックボックス・検索バッジ）
 * 「商談なし」だと商談が存在しない案件に読めるため、紐付いていないことを表す語にする
 */
export const INQUIRY_NO_DEAL_LABEL = '商談未紐付け'

/**
 * 日付フォーマット（YYYY-MM-DD）
 */
export const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

/**
 * 数値範囲制約
 */
export const FIELD_RANGES = {
  /** 単価上限 */
  UNIT_PRICE_MAX: 10_000_000,
  /** アサイン人月 下限 */
  ASSIGN_PERSON_MONTH_MIN: 0,
  /** アサイン人月 上限 */
  ASSIGN_PERSON_MONTH_MAX: 1.5,
} as const

/**
 * キャッシュ有効期間（秒）
 */
export const CACHE_TTL = {
  /** マスタデータ（1時間） */
  MASTER_DATA: 3600,
} as const
